import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Avatar, 
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions
} from '@mui/material';
import { Add, Person, PersonAdd, MoreVert, Edit, Delete } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, useAuth } from '../../../firebase';
import { Missionary } from '../../Account/Mission/MissionaryArtTab';
import { checkAndUpdateOrderProgress } from '../../../utils/firebaseUtils';

interface MissionarySelectorProps {
  missionaries: Missionary[];
  onSelect: (missionaryId: string) => void;
  orderId: string;
}

const MissionarySelector: React.FC<MissionarySelectorProps> = ({ missionaries, onSelect, orderId }) => {
  const { user } = useAuth();
  const [openNewDialog, setOpenNewDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  // Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMissionary, setSelectedMissionary] = useState<Missionary | null>(null);
  
  // Dialog States
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editName, setEditName] = useState('');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, missionary: Missionary) => {
    event.stopPropagation(); // Prevent card click
    setAnchorEl(event.currentTarget);
    setSelectedMissionary(missionary);
  };

  const handleMenuClose = (event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
    // Don't clear selectedMissionary yet if opening a dialog
  };

  const startEdit = () => {
    if (selectedMissionary) {
       setEditName(selectedMissionary.name);
       setOpenEditDialog(true);
    }
    handleMenuClose();
  };

  const startDelete = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleRename = async () => {
    if (!user || !selectedMissionary || !editName.trim()) return;
    
    try {
      const userRef = doc(db, "users", user.uid);
      
      // Firestore array updates are tricky for objects. 
      // We must remove the old object and add the new one.
      // 1. Remove Old
      await updateDoc(userRef, {
        missionaries: arrayRemove(selectedMissionary)
      });
      
      // 2. Add New
      const updatedMissionary = { ...selectedMissionary, name: editName };
      await updateDoc(userRef, {
        missionaries: arrayUnion(updatedMissionary)
      });
      
      // Force refresh or optimistic update would be ideal here if parent doesn't auto-refresh
      window.location.reload(); // Simple brute force for now to ensure consistency
      
    } catch (err) {
      console.error("Error renaming", err);
    }
  };

  const handleDelete = async () => {
    if (!user || !selectedMissionary) return;
    
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        missionaries: arrayRemove(selectedMissionary)
      });
      window.location.reload();
    } catch (err) {
      console.error("Error deleting", err);
    }
  };

  // ... (Keep existing handleSelect and handleCreateNew)
  // ... (But ensure handleSelect is robust)
  
  // Link existing missionary to this order (if order exists), then select
  const handleSelect = async (id: string) => {
    try {
      if (orderId) {
        const orderRef = doc(db, "orders", orderId);
        const selectedM = missionaries.find(m => m.id === id);
        
        // Update Order Link AND RESET STATUS
        await updateDoc(orderRef, {
          linkedMissionaryId: id,
          linkedMissionaryName: selectedM?.name || "Missionary Project",
          "formStatus.Missionary": false, // RESET status to force re-check
          lastFormUpdate: serverTimestamp()
        });
        
        // Trigger generic progress check to reflect the "false" status
        await checkAndUpdateOrderProgress(orderId, { Missionary: false });
      }
      onSelect(id);
    } catch (error) {
      console.error("Error linking missionary:", error);
    }
  };

  // Create new global missionary and link it (if order exists)
  const handleCreateNew = async () => {
    if (!user || !newName.trim()) return;
    setCreating(true);
    try {
      const newId = `missionary-${Date.now()}`;
      const newMissionary: Missionary = {
        id: newId,
        name: newName,
        notes: "",
        imageFilePath: "",
        missionDetails: { missionName: "", start: "", end: "", president: "" },
        companionList: []
      };

      // Add to Global User List
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        missionaries: arrayUnion(newMissionary)
      });

      // Link to Order if applicable
      if (orderId) {
        const orderRef = doc(db, "orders", orderId);
        await updateDoc(orderRef, {
          linkedMissionaryId: newId,
          linkedMissionaryName: newName,
          "formStatus.Missionary": false, // RESET status
          lastFormUpdate: serverTimestamp()
        });
        await checkAndUpdateOrderProgress(orderId, { Missionary: false });
        onSelect(newId);
      } else {
        // Just select (open editor)
        onSelect(newId);
      }
      
      setOpenNewDialog(false);
    } catch (error) {
      console.error("Error creating missionary:", error);
    } finally {
      setCreating(false);
    }
  };

  const getTitle = () => {
     if (missionaries.length === 0) {
        return "Let's create your first missionary profile.";
     }
     return orderId 
        ? "Select a missionary for this order" 
        : "Manage your missionary profiles";
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom align="center" sx={{ mb: 4 }}>
        {getTitle()}
      </Typography>

      <Grid container spacing={3} justifyContent="center">
        {missionaries.map((missionary) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={missionary.id}>
            <Card sx={{ height: '100%', border: '1px solid #ddd', position: 'relative' }}>
              <IconButton 
                 sx={{ position: 'absolute', top: 5, right: 5, zIndex: 10 }}
                 onClick={(e) => handleMenuOpen(e, missionary)}
              >
                 <MoreVert />
              </IconButton>
              
              <CardActionArea 
                onClick={() => handleSelect(missionary.id)}
                sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <Avatar sx={{ width: 80, height: 80, mb: 2, bgcolor: 'primary.main' }}>
                   {missionary.name.charAt(0)}
                </Avatar>
                <Typography variant="h6">{missionary.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {missionary.missionDetails.missionName || "No mission name"}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        ))}

        {/* Create New Card */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card sx={{ height: '100%', border: '2px dashed #ccc', bgcolor: 'transparent' }} elevation={0}>
             <CardActionArea 
                onClick={() => setOpenNewDialog(true)}
                sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
              >
                <PersonAdd sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">Add New Missionary</Typography>
              </CardActionArea>
          </Card>
        </Grid>
      </Grid>
      
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleMenuClose()}
      >
         <MenuItem onClick={startEdit}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText>Rename</ListItemText>
         </MenuItem>
         <MenuItem onClick={startDelete} sx={{ color: 'error.main' }}>
            <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
         </MenuItem>
      </Menu>

      {/* New Missionary Dialog */}
      <Dialog open={openNewDialog} onClose={() => setOpenNewDialog(false)}>
        <DialogTitle>Add New Missionary</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Missionary Name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateNew} disabled={creating} variant="contained">
            {orderId ? "Create & Select" : "Create Profile"}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Rename Dialog */}
       <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Rename Missionary</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleRename} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>Delete Missionary?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <b>{selectedMissionary?.name}</b>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            This action cannot be undone. All data and photos associated with this missionary profile will be lost forever.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete Forever
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MissionarySelector;
